// components/extractions/output/OutputMappingsPage.jsx
import React, { Suspense, useCallback, useEffect, useMemo, useState, useRef } from 'react';
// import debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';
import { createColumnHelper, useReactTable, getCoreRowModel } from '@tanstack/react-table';
import TableSkeleton from '../../../components/TableSkeleton';
import FiltersSkeleton from '../../../components/FiltersSkeleton';
import ExtractionLayout from '../ExtractionLayout';
import { ExtractionsProvider } from '../../../contexts/extractions/ExtractionContext';
import { ExtCustomColumnsProvider } from '../../../contexts/extractions/ExtCustomColumnContext';
import { ExtCustomColumnQueriesProvider } from '../../../contexts/extractions/ExtCustomColumnQueryContext';
import { ExtOutputMappingProvider, useExtOutputMapping } from '../../../contexts/extractions/ExtOutputMappingContext';
// import { viewFullData } from '../../../store/extractions/action';
import { useNavigate, useParams } from 'react-router-dom';
// import constants from '../../../commonUtils/constants';
import { getExtVersionFromKey, recalculateExtVersion } from '../../../store/extVersions/actions';

import { Button } from '../../../components/ui/button';
import IconButton from '../../../components/IconButton';
import { EyeIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import ReorderTable from '../../../components/ReorderTable';
import { setStepVal } from '../../../store/extractions/action';
import { cleanArray, spacesToUnderscores } from '../../../commonUtils/string.helper';
import { toast } from 'react-toastify';

// Separate the inner component that uses the context
const OutputMappingsContent = () => {
  const { key } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isEditMode, setIsEditMode] = useState(false);
  const [localTableData, setLocalTableData] = useState([]);
  const [orderChanged, setOrderChanged] = useState(false);
  const originalTableDataRef = useRef([]); // Store original order

  const { updateDataStrategy, getRowId } = useExtOutputMapping();
  const { extVersion } = useSelector((state) => state.ExtVersions);
  const { extractionViewOnly, extractionData } = useSelector((state) => state.Extractions);
  const { ExtOutputMappings, loading, errors } = useSelector((state) => state.ExtOutputMappings);
  const { FullDataList } = useSelector((state) => state.Extractions);

  const normalizedKey = useMemo(() => key?.replace('_', '-') || '', [key]);

  // 1. Load extVersion if not matching (existing logic — unchanged)
  useEffect(() => {
      if (normalizedKey && (!extVersion || !extVersion.key || extVersion.key !== normalizedKey)) {
          dispatch(getExtVersionFromKey({ key: normalizedKey, pageName: 'outputMappings', navigate }));
      }
      dispatch(setStepVal(4));
  }, [normalizedKey, extVersion, dispatch, navigate]);

  // 2. Always fetch output mappings when extVersion is resolved and matches
  //    This fires on mount AND whenever extVersion changes (including back-navigation)
  useEffect(() => {
      if (!extVersion?.id || !extVersion?.key) return;
      if (extVersion.key !== normalizedKey) return;

      dispatch({
          type: 'GET_OUTPUT_MAPPINGS',
          payload: {
              id: extVersion.id,
              key: extVersion.key,
          }
      });
  }, [extVersion?.id, extVersion?.key, normalizedKey, dispatch]);

  const toggleEditMode = () => {
    setIsEditMode(prev => {
      const newEditMode = !prev;
      
      // If exiting edit mode, restore original order
      if (!newEditMode && orderChanged) {
        setLocalTableData(originalTableDataRef.current);
        setOrderChanged(false);
      }
      
      return newEditMode;
    });
  };

  // Only navigate, let ViewFullDataContext handle data fetching
    const handleViewFullData = useCallback((dataGroup, dataCategory, extOutputMappingId) => {
      const selectedExtVersionKey = extVersion?.key || FullDataList?.meta?.extVersion?.key;
      if (selectedExtVersionKey && dataGroup?.title && dataCategory?.title) {
        navigate(
          `/extraction/view-full-data/${selectedExtVersionKey}/${spacesToUnderscores(dataGroup.title)}/${spacesToUnderscores(dataCategory.title)}`
        );
      }
    }, [extVersion, navigate, FullDataList?.meta?.extVersion?.key]);

  // Transform data for table
  const { tableData, dataGroupHeaders, groupIdMap } = useMemo(() => {
      if (!ExtOutputMappings?.data) return { tableData: [], dataGroupHeaders: [], groupIdMap: new Map() };

      const data = ExtOutputMappings.data;

      // Get unique groups sorted by priority
      // Use snapshot fields (data_group_priority, data_group_title) as fallback
      // when the populate reference is null (e.g. group was deleted after mapping was stored)
      const groupsMap = new Map();
      data.forEach(m => {
          const grpId = m.data_group?._id || m.data_group_title;
          const grpTitle = m.data_group?.title || m.data_group_title;
          if (grpId && grpTitle && !groupsMap.has(grpId)) {
              groupsMap.set(grpId, {
                  id: m.data_group?._id || null,
                  title: grpTitle,
                  priority: m.data_group_priority ?? 0
              });
          }
      });
      const headers = Array.from(groupsMap.values())
          .sort((a, b) => a.priority - b.priority)
          .map(g => g.title);

      // Create title-to-id map for groups
      const groupIdMap = new Map();
      groupsMap.forEach(group => groupIdMap.set(group.title, group.id));

      // Get unique categories sorted by snapshot priority
      // Use snapshot fields (data_category_priority, data_category_title) as fallback
      // when the populate reference is null (e.g. category was deleted after mapping was stored)
      const categoriesMap = new Map();
      data.forEach(m => {
          const catId = m.data_category?._id || m.data_category_title;
          const catTitle = m.data_category?.title || m.data_category_title;
          if (catId && catTitle && !categoriesMap.has(catId)) {
              categoriesMap.set(catId, {
                  id: m.data_category?._id || null,
                  title: catTitle,
                  priority: m.data_category_priority ?? 0
              });
          }
      });
      const categories = Array.from(categoriesMap.values())
          .sort((a, b) => a.priority - b.priority);

      // Create lookup map: category title -> group title -> count
      // Use snapshot titles as fallback when populate reference is null
      const dataMap = new Map();
      data.forEach(m => {
          const catTitle = m.data_category?.title || m.data_category_title;
          const grpTitle = m.data_group?.title || m.data_group_title;
          if (!catTitle || !grpTitle) return;
          if (!dataMap.has(catTitle)) {
              dataMap.set(catTitle, new Map());
          }
          dataMap.get(catTitle).set(grpTitle, m.count);
      });

      // Build table rows with IDs
      const rows = categories.map((cat, idx) => {
          const row = {
              id: `${cat.id}_${idx}`,
              _id: cat.id,
              rowIndex: idx + 1,
              data_category: cat.title,
              data_category_id: cat.id,
          };
          // Add count for each group header
          headers.forEach(grpTitle => {
              row[grpTitle] = dataMap.get(cat.title)?.get(grpTitle) || 0;
          });
          return row;
      });

      return { tableData: rows, dataGroupHeaders: headers, groupIdMap };
  }, [ExtOutputMappings]);

  // Update local table data when tableData changes
  useEffect(() => {
    if (tableData.length > 0) {
      setLocalTableData(tableData);
      originalTableDataRef.current = tableData; // Store original order
      setOrderChanged(false); // Reset order changed flag
    }
  }, [tableData]);

  // Use local data for display
  const displayTableData = localTableData.length > 0 ? localTableData : tableData;

  // Check if order has changed
  const checkOrderChanged = useCallback((newData) => {
    if (originalTableDataRef.current.length !== newData.length) {
      return true;
    }
    
    // Compare the order of category IDs
    for (let i = 0; i < newData.length; i++) {
      if (originalTableDataRef.current[i]?.data_category_id !== newData[i]?.data_category_id) {
        return true;
      }
    }
    
    return false;
  }, []);

  // Handle row reorder - only update local state
  const handleRowReorder = useCallback((reorderedData) => {
    setLocalTableData(reorderedData);
    setOrderChanged(checkOrderChanged(reorderedData));
  }, [checkOrderChanged]);

  // Handle recalculate button click
  const handleRecalculate = useCallback(async () => {
    if (!orderChanged || localTableData.length === 0) return;
    
    // Extract unique categories with their new priorities based on row order
    const seenCategories = new Set();
    const orderData = [];
    
    localTableData.forEach((item, index) => {
      const categoryId = item.data_category_id || item._id;
      
      if (categoryId && !seenCategories.has(categoryId)) {
        seenCategories.add(categoryId);
        orderData.push({
          _id: categoryId,
          priority: orderData.length + 1
        });
      }
    });
    
    if (orderData.length > 0) {
      if (!extractionData?.id) {
        toast.error('Extraction ID not found. Please refresh and try again.');
        return;
      }

      try {
        const cleanedExtVersion = { ...extVersion };
        delete cleanedExtVersion.loading;

        // Dispatch recalculate which hits the atomic backend endpoint
        dispatch(
          recalculateExtVersion({
            extId: extractionData.id,
            versionId: extVersion.id,
            formData: {
              orderData,
              selected_columns: cleanArray(cleanedExtVersion.selected_columns),
              custom_columns: cleanArray(cleanedExtVersion.custom_columns) || [],
              is_default: cleanedExtVersion.is_default
            },
            navigate,
            path: `/extraction/${extractionData.key}`
          })
        );

        // Optimistically reset local UI state
        originalTableDataRef.current = localTableData;
        setOrderChanged(false);
        setIsEditMode(false);
      } catch (error) {
        const message = error?.response?.data?.message || 'Could not update data category order.';
        toast.error(message);
      }
    }
  }, [orderChanged, localTableData, extVersion, dispatch, navigate, extractionData.id, extractionData.key]);

  // Create columns
  const columnHelper = useMemo(() => createColumnHelper(), []);
  
  const columns = useMemo(() => {
      const cols = [
          columnHelper.accessor('rowIndex', {
              header: '#',
              cell: info => <div className="text-center">{info.row.index + 1}</div>
          }),
          columnHelper.accessor('data_category', {
              header: 'Data Category',
              cell: info => <div className="font-medium">{info.getValue()}</div>
          }),
      ];

      dataGroupHeaders.forEach(header => {
          cols.push(
              columnHelper.accessor(header, {
                  header: header,
                  cell: info => {
                      const count = info.getValue();
                      const dataGroup = {
                        id: groupIdMap.get(header),
                        title: header
                      }
                      const dataCategory = {
                        id: info.row.original.data_category_id,
                        title: info.row.original.data_category
                      }
                      
                      return (
                            <div
                              className={
                              `text-center font-semibold ${count > 0 ? 'text-blue-600 cursor-pointer hover:text-blue-800 hover:underline' : 'text-gray-400 cursor-default'}`
                              }
                              onClick={count > 0 && !isEditMode ? () => handleViewFullData(dataGroup, dataCategory) : undefined}
                            >
                              {count?.toLocaleString() || '0'}
                          </div>
                      );
                  }
              })
          );
      });

      return cols;
  }, [dataGroupHeaders, columnHelper, handleViewFullData, groupIdMap, isEditMode]);

  const table = useReactTable({
      data: displayTableData,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getRowId: getRowId,
  });

  if (errors) return <div className="text-red-500 text-center py-10">Error: {errors.message}</div>;

  return (
    <ExtractionLayout hideRightButton={true}>
        <div>
            <div className="flex justify-between mb-4 hidden md:flex">
                <Suspense fallback={<FiltersSkeleton/>}>
                    <div></div>
                </Suspense>
                <div className="flex items-start mb-4">
                    <Button variant="border_outline" onClick={updateDataStrategy} disabled={extractionViewOnly || loading} className="ml-2">Update Data Strategy</Button>
                    <IconButton
                      variant="menu" 
                      size="icon" 
                      rounded="sm" 
                      className="ml-2" 
                      disabled={extractionViewOnly}
                      Icon={isEditMode ? EyeIcon : PencilSquareIcon} 
                      onClick={toggleEditMode} 
                    />
                </div>
            </div>
            <div className="flex flex-col gap-4 justify-between mb-4 md:hidden">
                {/* Mobile layout if needed */}
            </div>
        </div>
        <Suspense fallback={<TableSkeleton columns={dataGroupHeaders.length + 2} rows={5} headers={["#", "Data Category", ...dataGroupHeaders]}/>}> 
            <ReorderTable
                loading={loading}
                table={table}
                data={displayTableData || []}
                columns={columns}
                enableRowReordering={isEditMode}
                onRowReorder={handleRowReorder}
                getRowId={getRowId}
                pagination={false}
                notfoundMsg="No extraction data found"
            />
        </Suspense>
        {isEditMode && (
          <div className="flex justify-end mt-4">
              <Button onClick={handleRecalculate} disabled={!orderChanged}>
                Recalculate
              </Button>
          </div>
        )}
    </ExtractionLayout>
  );
};

// Main component that wraps everything with providers
const OutputMappingsPage = () => {
  return (
    <ExtractionsProvider>
      <ExtCustomColumnsProvider>
        <ExtCustomColumnQueriesProvider>
          <ExtOutputMappingProvider>
            <OutputMappingsContent />
          </ExtOutputMappingProvider>
        </ExtCustomColumnQueriesProvider>
      </ExtCustomColumnsProvider>
    </ExtractionsProvider>
  );
};

export default OutputMappingsPage;